import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Download } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { Product } from '@/hooks/useProducts';
import { exampleFilesByProduct } from '@/data/exampleFiles';

interface ProductInfoSectionsProps {
  product: Product;
  /** When true, wraps each section with a top border. Defaults to true. */
  bordered?: boolean;
  /** Render the "Available countries" flags block. Defaults to true. */
  showCountries?: boolean;
  /** Render the "Example files" download list. Defaults to true. */
  showExamples?: boolean;
}

export function ProductInfoSections({
  product,
  bordered = true,
  showCountries = true,
  showExamples = true,
}: ProductInfoSectionsProps) {
  const { t } = useTranslation();
  const [showFileDetails, setShowFileDetails] = useState(false);
  const [showRangeTypes, setShowRangeTypes] = useState(false);
  const [showColumnTypes, setShowColumnTypes] = useState(false);

  const hasFileColumns = product.file_columns && product.file_columns.length > 0;
  const hasStartLength = hasFileColumns && product.file_columns!.some(
    (col) => col.start != null || col.length != null
  );
  const isAddressOrPostal = product.ws_category === 'addresses' || product.ws_category === 'postalCodes';
  const productExamples = exampleFilesByProduct[product.slug];
  const hasCountries = product.available_countries && product.available_countries.length > 0;
  const hasExamples = productExamples && productExamples.length > 0;

  const flag: Record<string, string> = { SE: '🇸🇪', NO: '🇳🇴', DK: '🇩🇰', FI: '🇫🇮' };
  const name: Record<string, string> = { SE: 'Sverige', NO: 'Norge', DK: 'Danmark', FI: 'Finland' };

  const sectionClass = bordered ? 'border-t border-border pt-3' : '';

  const renderCountries = showCountries && hasCountries;
  const renderExamples = showExamples && hasExamples;

  return (
    <div className="space-y-4">
      {(renderCountries || renderExamples) && (
        <div className="flex flex-wrap gap-x-8 gap-y-3">
          {renderCountries && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {t('dataFiles.specs.availableCountries')}
              </p>
              <div className="flex gap-2 items-center">
                {product.available_countries!.map((code) => {
                  const upper = code.toUpperCase();
                  if (!flag[upper]) return null;
                  return (
                    <span key={upper} className="inline-flex items-center gap-1 text-sm" title={name[upper]}>
                      <span className="text-lg">{flag[upper]}</span>
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {renderExamples && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {t('dataFiles.specs.exampleFiles')}
              </p>
              <div className="flex flex-col gap-0.5">
                {productExamples!.map((file) => (
                  <a
                    key={file.url}
                    href={file.url}
                    download
                    className="inline-flex items-center gap-1 text-xs text-secondary hover:underline"
                  >
                    <Download className="w-3 h-3" />
                    {file.label}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {hasFileColumns && (
        <Collapsible open={showFileDetails} onOpenChange={setShowFileDetails}>
          <div className={sectionClass}>
            <CollapsibleTrigger className="flex items-center gap-1.5 text-sm font-medium text-secondary hover:underline cursor-pointer">
              <ChevronRight className={cn('w-4 h-4 transition-transform', showFileDetails && 'rotate-90')} />
              {showFileDetails
                ? t('dataFiles.specs.hideFileContents')
                : t('dataFiles.specs.showFileContents')}
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-3">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="h-8 text-xs">{t('admin.columnName')}</TableHead>
                      <TableHead className="h-8 text-xs">{t('dataFiles.specs.description')}</TableHead>
                      {hasStartLength && (
                        <>
                          <TableHead className="h-8 text-xs w-20">{t('dataFiles.specs.start')}</TableHead>
                          <TableHead className="h-8 text-xs w-20">{t('dataFiles.specs.length')}</TableHead>
                        </>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {product.file_columns!.map((col, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="py-1.5 text-sm font-medium">{col.name}</TableCell>
                        <TableCell className="py-1.5 text-sm text-muted-foreground">{col.description || '—'}</TableCell>
                        {hasStartLength && (
                          <>
                            <TableCell className="py-1.5 text-sm font-mono">{col.start ?? '—'}</TableCell>
                            <TableCell className="py-1.5 text-sm font-mono">{col.length ?? '—'}</TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      )}

      {isAddressOrPostal && (
        <Collapsible open={showRangeTypes} onOpenChange={setShowRangeTypes}>
          <div className={sectionClass}>
            <CollapsibleTrigger className="flex items-center gap-1.5 text-sm font-medium text-secondary hover:underline cursor-pointer">
              <ChevronRight className={cn('w-4 h-4 transition-transform', showRangeTypes && 'rotate-90')} />
              {showRangeTypes
                ? t('dataFiles.specs.hideRangeTypes')
                : t('dataFiles.specs.showRangeTypes')}
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-3">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="h-8 text-xs w-16">{t('dataFiles.specs.rangeCode')}</TableHead>
                      <TableHead className="h-8 text-xs">{t('dataFiles.specs.description')}</TableHead>
                      <TableHead className="h-8 text-xs">{t('dataFiles.specs.example')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[
                      { code: 'NU', desc: 'Udda gatunummer', example: 'Storgatan 11, 117' },
                      { code: 'NJ', desc: 'Jämna gatunummer', example: 'Storgatan 12, 188' },
                      { code: 'NO', desc: 'Öppen numrering (främst Box)', example: 'Box 3002' },
                      { code: 'NA', desc: 'Äldre, ej fastställda adresser', example: 'Charlottendal 123' },
                      { code: 'AM', desc: 'Mätpunktsadresser (MMMM-SSSS)', example: 'Landsvägen 112-34' },
                      { code: 'AF', desc: 'Fri intervall (andra postoperatörer)', example: 'Box 19000, Port A' },
                      { code: 'AB', desc: 'Adresser utan numrering', example: 'Vårdinge Kyrka' },
                    ].map((row) => (
                      <TableRow key={row.code}>
                        <TableCell className="py-1.5 text-sm font-mono font-medium">{row.code}</TableCell>
                        <TableCell className="py-1.5 text-sm text-muted-foreground">{row.desc}</TableCell>
                        <TableCell className="py-1.5 text-sm text-muted-foreground italic">{row.example}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      )}

      {isAddressOrPostal && (
        <Collapsible open={showColumnTypes} onOpenChange={setShowColumnTypes}>
          <div className={sectionClass}>
            <CollapsibleTrigger className="flex items-center gap-1.5 text-sm font-medium text-secondary hover:underline cursor-pointer">
              <ChevronRight className={cn('w-4 h-4 transition-transform', showColumnTypes && 'rotate-90')} />
              {showColumnTypes
                ? t('dataFiles.specs.hideColumnTypes')
                : t('dataFiles.specs.showColumnTypes')}
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-3">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="h-8 text-xs w-16">{t('dataFiles.specs.rangeCode')}</TableHead>
                      <TableHead className="h-8 text-xs">{t('dataFiles.specs.addressType')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[
                      { code: 'AT', desc: 'Tätort' },
                      { code: 'AL', desc: 'Landsbygd' },
                      { code: 'AO', desc: 'Boxadresser' },
                      { code: 'BO', desc: 'Box via andra operatörer' },
                      { code: 'PO', desc: 'Andra postleverantörer' },
                      { code: 'AS', desc: 'Större företag med eget postnummer' },
                      { code: 'AR', desc: 'Postenheter, sorteringscenter' },
                      { code: 'AN', desc: 'Administrativ post (tävlingspost)' },
                      { code: 'AU', desc: 'Svarspost/frisvar' },
                    ].map((row) => (
                      <TableRow key={row.code}>
                        <TableCell className="py-1.5 text-sm font-mono font-medium">{row.code}</TableCell>
                        <TableCell className="py-1.5 text-sm text-muted-foreground">{row.desc}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      )}
    </div>
  );
}
